import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, relTime } from '../lib/api'
import { FromAddressPicker, useIsOperator } from '../components/FromAddress'
import { cm, cmBadge, type CampaignFields, type CmCampaignDetail, type CmSpamCheck } from '../lib/cm'

/**
 * Campaigns sent to a list through one mailbox's own SMTP. Sending is queued and throttled per
 * SMTP host (the cPanel account's hourly cap covers all its domains), so a large list takes
 * hours, not seconds — the page shows the queue and an estimate rather than pretending otherwise.
 */
export function Campaigns() {
  const navigate = useNavigate()
  const { data, isLoading } = useQuery({ queryKey: ['cm', 'campaigns'], queryFn: cm.campaigns, refetchInterval: 15_000 })
  const create = useMutation({
    mutationFn: () => cm.createCampaign({ name: 'Untitled campaign' }),
    onSuccess: r => navigate(`/campaigns/${r.id}`),
  })
  const rows = data?.data ?? []

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>Campaigns</h1>
          <p className="lede">Newsletters and announcements to your lists, sent from your own verified domain within your daily marketing allowance.</p>
        </div>
        <button onClick={() => create.mutate()} disabled={create.isPending}>New campaign</button>
      </div>

      {isLoading && <div className="skeleton" style={{ height: 200 }} />}
      {!isLoading && rows.length === 0 && (
        <div className="empty"><div className="big">✈</div><p><strong>No campaigns yet</strong></p>
          <p>You need a verified <Link to="/domains">sending domain</Link> and a <Link to="/lists">list</Link> with subscribers.</p></div>
      )}
      {rows.length > 0 && (
        <div className="card flush">
          <table className="data">
            <thead><tr><th>Campaign</th><th>Status</th><th>List</th><th>Progress</th><th>Bounced</th><th>Unsubscribed</th><th>Updated</th></tr></thead>
            <tbody>
              {rows.map(c => {
                const b = cmBadge(c.status)
                return (
                  <tr key={c.id}>
                    <td><Link to={`/campaigns/${c.id}`}><strong>{c.name}</strong></Link>
                      <div className="small muted truncate">{c.subject || '(no subject)'}</div></td>
                    <td><span className={`badge ${b.cls}`}>{b.text}</span></td>
                    <td className="small">{c.list_name ?? '—'}</td>
                    <td className="small">{c.total > 0 ? `${c.sent} / ${c.total}` : '—'}</td>
                    <td className="small">{c.bounced}</td>
                    <td className="small">{c.unsubscribed}</td>
                    <td className="small muted">{relTime(c.updated_at)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export function CampaignDetail() {
  const { id = '' } = useParams()
  const { data, isLoading } = useQuery({
    queryKey: ['cm', 'campaign', id], queryFn: () => cm.campaign(id),
    refetchInterval: q => (['sending', 'scheduled'].includes(q.state.data?.campaign.status ?? '') ? 10_000 : false),
  })

  if (isLoading || !data) return <div className="skeleton" style={{ height: 400 }} />
  const c = data.campaign
  const b = cmBadge(c.status)

  return (
    <div>
      <p><Link to="/campaigns">← Campaigns</Link></p>
      <div className="page-head">
        <div>
          <h1>{c.name} <span className={`badge ${b.cls}`} style={{ verticalAlign: 'middle' }}>{b.text}</span></h1>
          <p className="lede">
            {c.list_name ? <>To <strong>{c.list_name}</strong> ({data.eligible_recipients} subscribed)</> : 'No list chosen'}
            {(c.mailbox || c.from_email) && <> · from <span className="mono">{c.mailbox ?? c.from_email}</span></>}
          </p>
        </div>
      </div>
      {c.status === 'draft' || c.status === 'scheduled'
        ? <Editor detail={data} />
        : <Progress detail={data} />}
    </div>
  )
}

function Editor({ detail }: { detail: CmCampaignDetail }) {
  const c = detail.campaign
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { data: lists } = useQuery({ queryKey: ['cm', 'lists'], queryFn: cm.lists })
  const operator = useIsOperator()
  const { data: boxes } = useQuery({ queryKey: ['cm', 'mailboxes'], queryFn: cm.mailboxes, enabled: operator })

  const [f, setF] = useState<Required<CampaignFields>>({
    name: c.name, list_id: c.list_id ?? '', mailbox_id: c.mailbox_id ?? '', from_email: c.from_email ?? '', from_name: c.from_name ?? '',
    reply_to: c.reply_to ?? '', subject: c.subject ?? '', html: c.html ?? '', text: c.text ?? '',
    footer_address: c.footer_address ?? '',
  })
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null)
  const [testTo, setTestTo] = useState('')
  const [sendAt, setSendAt] = useState('')

  const set = (k: keyof CampaignFields) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setF({ ...f, [k]: e.target.value })
  const fail = (e: unknown) => setMsg({ kind: 'error', text: (e as ApiError).message })
  const refresh = () => qc.invalidateQueries({ queryKey: ['cm'] })

  const save = useMutation({
    mutationFn: () => cm.updateCampaign(c.id, f),
    onSuccess: () => { setMsg({ kind: 'ok', text: 'Saved.' }); refresh() },
    onError: fail,
  })
  const test = useMutation({
    // Saves first, so the test is always of what is on screen.
    mutationFn: async () => { await cm.updateCampaign(c.id, f); return cm.testCampaign(c.id, testTo) },
    onSuccess: () => { setMsg({ kind: 'ok', text: `Test sent to ${testTo}. Check the inbox AND the spam folder.` }) },
    onError: fail,
  })
  const schedule = useMutation({
    mutationFn: async () => {
      await cm.updateCampaign(c.id, f)
      return cm.schedule(c.id, sendAt ? new Date(sendAt).toISOString() : undefined)
    },
    onSuccess: () => { setMsg(null); refresh() },
    onError: fail,
  })
  const unschedule = useMutation({ mutationFn: () => cm.pause(c.id), onSuccess: refresh, onError: fail })
  const remove = useMutation({ mutationFn: () => cm.deleteCampaign(c.id), onSuccess: () => navigate('/campaigns'), onError: fail })

  const sendable = (boxes?.data ?? []).filter(b => b.sending_enabled && !b.auth_failed)
  const etaHours = detail.host_usage ? Math.ceil(detail.eligible_recipients / Math.max(1, detail.host_usage.hourly_cap)) : null

  return (
    <div className="dash-cols" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 20 }}>
      <div>
        {c.status === 'scheduled' && (
          <div className="alert" style={{ marginBottom: 16 }}>
            Scheduled for {new Date(c.scheduled_at!).toLocaleString()}.{' '}
            <button className="ghost" onClick={() => unschedule.mutate()}>Pause</button>
          </div>
        )}
        <div className="card">
          <div className="field"><label>Campaign name (internal)</label><input value={f.name} onChange={set('name')} /></div>
          <div className="row" style={{ flexWrap: 'wrap' }}>
            <div className="field" style={{ flex: '1 1 200px' }}><label>List</label>
              <select value={f.list_id} onChange={set('list_id')}>
                <option value="">— choose —</option>
                {(lists?.data ?? []).map(l => <option key={l.id} value={l.id}>{l.name} ({l.subscribed})</option>)}
              </select></div>
            <div className="field" style={{ flex: '1 1 260px' }}><label>From address</label>
              <FromAddressPicker value={f.from_email} onChange={v => setF(x => ({ ...x, from_email: v, mailbox_id: v ? '' : x.mailbox_id }))} /></div>
            {operator && (
              <div className="field" style={{ flex: '1 1 200px' }}><label>…or a connected mailbox (operator)</label>
                <select value={f.mailbox_id} onChange={e => setF(x => ({ ...x, mailbox_id: e.target.value, from_email: e.target.value ? '' : x.from_email }))}>
                  <option value="">— none —</option>
                  {sendable.map(b => <option key={b.id} value={b.id}>{b.email}</option>)}
                </select></div>
            )}
          </div>
          <div className="row" style={{ flexWrap: 'wrap' }}>
            <div className="field" style={{ flex: '1 1 200px' }}><label>From name</label><input value={f.from_name} onChange={set('from_name')} placeholder="Bing My Business" /></div>
            <div className="field" style={{ flex: '1 1 200px' }}><label>Reply-To (optional)</label><input type="email" value={f.reply_to} onChange={set('reply_to')} placeholder="hello@…" /></div>
          </div>
          <div className="field"><label>Subject</label><input value={f.subject} onChange={set('subject')} placeholder="Hi {{first_name}}, …" /></div>
          <div className="field"><label>Email body (HTML)</label>
            <textarea rows={14} className="mono small" value={f.html} onChange={set('html')}
                      placeholder={'<p>Hi {{first_name}},</p>\n<p>…</p>'} /></div>
          <div className="field"><label>Plain-text version (optional — generated from the HTML if empty)</label>
            <textarea rows={4} value={f.text} onChange={set('text')} /></div>
          <div className="field"><label>Footer: your postal / contact address (required by law)</label>
            <textarea rows={2} value={f.footer_address} onChange={set('footer_address')} placeholder={'Bing My Business Ltd, 1 High Street, London, UK'} /></div>
          <p className="small muted">
            Placeholders: <span className="mono">{'{{name}} {{first_name}} {{email}} {{unsubscribe_url}}'}</span>.
            An unsubscribe link and the footer are added to every email automatically.
          </p>
          {msg && <div className={`alert ${msg.kind === 'error' ? 'error' : ''}`}>{msg.text}</div>}
          <div className="row" style={{ flexWrap: 'wrap' }}>
            <button onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save draft'}</button>
            {c.status === 'draft' && <button className="ghost" onClick={() => { if (confirm('Delete this draft?')) remove.mutate() }}>Delete</button>}
          </div>
        </div>

        <div className="card" style={{ marginTop: 16 }}>
          <h3 style={{ marginTop: 0 }}>1. Send yourself a test</h3>
          <form className="row" onSubmit={e => { e.preventDefault(); test.mutate() }}>
            <input type="email" required placeholder="you@gmail.com" value={testTo} onChange={e => setTestTo(e.target.value)} />
            <button type="submit" className="secondary" disabled={test.isPending}>{test.isPending ? 'Sending…' : 'Send test'}</button>
          </form>
          <p className="small muted">Send one to Gmail and one to Outlook, and check that it lands in the inbox, not spam.</p>
          <SpamCheck id={c.id} save={() => cm.updateCampaign(c.id, f)} />

          <h3>2. Send to the list</h3>
          <div className="row" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="field" style={{ marginBottom: 0 }}><label>Start at (leave empty for now)</label>
              <input type="datetime-local" value={sendAt} onChange={e => setSendAt(e.target.value)} /></div>
            <button disabled={schedule.isPending || detail.eligible_recipients === 0} onClick={() => {
              if (confirm(`Send "${f.subject}" to ${detail.eligible_recipients} subscribers${sendAt ? ` starting ${new Date(sendAt).toLocaleString()}` : ' now'}?`)) schedule.mutate()
            }}>{schedule.isPending ? 'Scheduling…' : `Send to ${detail.eligible_recipients} subscribers`}</button>
          </div>
          {etaHours !== null && detail.eligible_recipients > 0 && (
            <p className="small muted">
              At {detail.host_usage!.hourly_cap} emails/hour this takes about {etaHours} hour{etaHours === 1 ? '' : 's'}
              {detail.eligible_recipients > detail.host_usage!.daily_cap ? `, spread over more than a day (daily cap ${detail.host_usage!.daily_cap})` : ''}.
            </p>
          )}
        </div>
      </div>

      <div>
        <div className="card flush" style={{ position: 'sticky', top: 16 }}>
          <div className="small muted" style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
            Preview · <strong>{f.subject || '(no subject)'}</strong>
          </div>
          <iframe title="Preview" sandbox="" style={{ width: '100%', height: 640, border: 0, background: '#fff' }}
                  srcDoc={`${(f.html || `<pre>${escapeHtml(f.text)}</pre>`)
                    .replace(/\{\{\s*(first_name|name)\s*\}\}/gi, 'Jane')
                    .replace(/\{\{\s*email\s*\}\}/gi, 'jane@example.com')}
                    <div style="margin-top:32px;padding-top:16px;border-top:1px solid #ddd;font:12px/1.5 Arial,sans-serif;color:#777">
                    ${escapeHtml(f.footer_address).replace(/\n/g, '<br>')}<br>You are receiving this because you subscribed. <u>Unsubscribe</u></div>`} />
        </div>
      </div>
    </div>
  )
}

function Progress({ detail }: { detail: CmCampaignDetail }) {
  const c = detail.campaign
  const qc = useQueryClient()
  const [status, setStatus] = useState('')
  const [msg, setMsg] = useState<string | null>(null)
  const { data: sends } = useQuery({
    queryKey: ['cm', 'sends', c.id, status],
    queryFn: () => cm.sends(c.id, { status: status || undefined, limit: 100 }),
    refetchInterval: c.status === 'sending' ? 10_000 : false,
  })
  const act = useMutation({
    mutationFn: (a: 'pause' | 'resume' | 'cancel') => cm[a](c.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cm'] }),
    onError: e => setMsg((e as ApiError).message),
  })
  const pct = c.total > 0 ? Math.round(((c.sent + c.failed + c.bounced) / c.total) * 100) : 0

  return (
    <>
      <div className="kpis">
        <Kpi label="Sent" value={`${c.sent} / ${c.total}`} sub={`${pct}% done`} />
        <Kpi label="Waiting in queue" value={detail.queued} sub={detail.eta_minutes > 0 ? `about ${fmtEta(detail.eta_minutes)} left` : undefined} />
        <Kpi label="Bounced" value={c.bounced} sub={c.sent > 0 ? `${((c.bounced / c.sent) * 100).toFixed(1)}% — keep under 2%` : undefined} />
        <Kpi label="Unsubscribed" value={c.unsubscribed} />
        <Kpi label="Failed" value={c.failed} />
      </div>

      {detail.delivery && (
        <div className="kpis" style={{ marginTop: 12 }}>
          <Kpi label="Delivered" value={detail.delivery.delivered}
               sub={detail.delivery.handed_over > 0 ? `${Math.round(detail.delivery.delivered / detail.delivery.handed_over * 100)}% of ${detail.delivery.handed_over}` : undefined} />
          <Kpi label="Opened" value={detail.delivery.opened}
               sub={detail.delivery.delivered > 0 ? `${Math.round(detail.delivery.opened / detail.delivery.delivered * 100)}% open rate` : undefined} />
          <Kpi label="Clicked" value={detail.delivery.clicked}
               sub={detail.delivery.delivered > 0 ? `${Math.round(detail.delivery.clicked / detail.delivery.delivered * 100)}% click rate` : undefined} />
          <Kpi label="In delivery" value={detail.delivery.in_queue + detail.delivery.deferred} sub={detail.delivery.deferred ? `${detail.delivery.deferred} retrying` : undefined} />
          <Kpi label="Spam complaints" value={detail.delivery.complained} sub="keep under 0.1%" />
        </div>
      )}

      {detail.host_usage && (
        <p className="small muted">
          {detail.host_usage.host}: {detail.host_usage.last_hour}/{detail.host_usage.hourly_cap} this hour,
          {' '}{detail.host_usage.last_day}/{detail.host_usage.daily_cap} in 24h (shared by every mailbox on that host).
        </p>
      )}

      <div className="row" style={{ margin: '12px 0 20px' }}>
        {c.status === 'sending' && <button className="secondary" onClick={() => act.mutate('pause')}>Pause</button>}
        {c.status === 'paused' && <button onClick={() => act.mutate('resume')}>Resume</button>}
        {(c.status === 'sending' || c.status === 'paused') && (
          <button className="ghost" onClick={() => { if (confirm('Cancel? Everyone not yet sent to will be skipped.')) act.mutate('cancel') }}>Cancel</button>
        )}
        {c.started_at && <span className="small muted">Started {relTime(c.started_at)}{c.finished_at ? ` · finished ${relTime(c.finished_at)}` : ''}</span>}
      </div>
      {msg && <div className="alert error">{msg}</div>}

      <div className="toolbar">
        <div className="segmented" role="tablist">
          {['', 'queued', 'sent', 'bounced', 'failed', 'skipped'].map(s => (
            <button key={s || 'all'} className={status === s ? 'active' : ''} onClick={() => setStatus(s)}>
              {s === '' ? 'All' : cmBadge(s).text}
            </button>
          ))}
        </div>
      </div>
      <div className="card flush">
        <table className="data">
          <thead><tr><th>Recipient</th><th>Status</th><th>Detail</th><th>When</th></tr></thead>
          <tbody>
            {(sends?.data ?? []).map(s => {
              const b = cmBadge(s.status)
              return (
                <tr key={s.email}>
                  <td className="mono small">{s.email}</td>
                  <td><span className={`badge ${b.cls}`}>{b.text}</span></td>
                  <td className="small muted truncate" title={s.error ?? ''}>{s.error ?? (s.attempts > 1 ? `${s.attempts} attempts` : '')}</td>
                  <td className="small muted">{s.sent_at ? relTime(s.sent_at) : s.next_attempt_at ? `next try ${relTime(s.next_attempt_at)}` : relTime(s.queued_at)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}

function Kpi({ label, value, sub }: { label: string; value: number | string; sub?: string }) {
  return (
    <div className="kpi"><div className="kpi-body">
      <span className="kpi-value">{typeof value === 'number' ? value.toLocaleString() : value}</span>
      <span className="kpi-label">{label}</span>
      {sub && <span className="kpi-sub">{sub}</span>}
    </div></div>
  )
}

function fmtEta(min: number) {
  if (min < 60) return `${min} min`
  const h = Math.floor(min / 60)
  return h < 48 ? `${h} h ${min % 60} min` : `${Math.round(h / 24)} days`
}

function escapeHtml(s: string) {
  return s.replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]!))
}

/** Scores the content with the same filter receivers use, before anything is sent. */
function SpamCheck({ id, save }: { id: string; save: () => Promise<unknown> }) {
  const [result, setResult] = useState<CmSpamCheck | null>(null)
  const [error, setError] = useState<string | null>(null)
  const check = useMutation({
    mutationFn: async () => { await save(); return cm.spamCheck(id) },
    onSuccess: r => { setResult(r); setError(null) },
    onError: e => setError((e as ApiError).message),
  })
  const tone = result?.verdict === 'good' ? 'ok' : result?.verdict === 'review' ? 'warn' : 'error'
  const label = result?.verdict === 'good' ? 'Looks clean' : result?.verdict === 'review' ? 'Worth a second look' : 'Likely to be filtered as spam'
  return (
    <div style={{ marginTop: 10 }}>
      <button type="button" className="ghost" disabled={check.isPending} onClick={() => check.mutate()}>
        {check.isPending ? 'Checking…' : 'Check spam score'}
      </button>
      {error && <div className="alert error" style={{ marginTop: 8 }}>{error}</div>}
      {result && (
        <div className={`alert ${tone}`} style={{ marginTop: 8 }}>
          <div><strong>{label}</strong> — content score {result.content_score} (lower is better; under 1 is good, 3 or more is risky)</div>
          {result.findings.length > 0 && (
            <ul className="small" style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {result.findings.map(f => <li key={f.name}><span className="mono">{f.name}</span> +{f.score.toFixed(1)}{f.description ? ` — ${f.description}` : ''}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
